import { type ToolCallMessagePartProps, useAuiState } from "@assistant-ui/react";
import { useContext } from "react";
import { CopilotPartRoleContext } from "@/features/copilot/components/copilot-part-role-context";
import { renderCopilotReadCard } from "@/features/copilot/components/read-cards/copilot-read-card";
import { readCopilotCardData } from "@/features/copilot/lib/copilot-read-card-data";
import { readCopilotPayload } from "@/features/copilot/lib/copilot-read-payload";
import {
  copilotToolActivityLabel,
  copilotToolState,
  isCopilotPlanningTool,
} from "@/features/copilot/lib/copilot-tool-labels";
import { LogoMark } from "@/shared/components/brand/logo-mark";
import { useActiveWorkspace } from "@/shared/hooks/use-active-workspace";
import { cn } from "@/shared/lib/cn";

/// A read that answers the question shows its data as a card; a read that only fed a later step
/// is one quiet line with the Pulsewise mark. The raw payload never reaches the transcript.
export function CopilotToolChip({
  toolName,
  toolCallId,
  args,
  result,
  isError,
  status,
}: ToolCallMessagePartProps) {
  const { workspace } = useActiveWorkspace();
  const role = useContext(CopilotPartRoleContext);
  const parts = useAuiState((state) => state.message.parts);
  if (role === "hidden" || isCopilotPlanningTool(toolName)) return null;
  const state = copilotToolState({ isError, statusType: status?.type, result });
  if (state === "done" && role !== "row") {
    const fromPart = readCopilotCardData(parts, toolCallId);
    // Fallback for conversations stored before card data parts (2026-10-11); delete after 30-day retention.
    const payload = fromPart
      ? { ...fromPart, input: args?.args }
      : readCopilotPayload(args, result);
    const card = payload ? renderCopilotReadCard(payload, workspace.slug) : null;
    if (card) return card;
  }
  return (
    <p
      data-state={state}
      className="flex items-center gap-1.5 text-xs leading-5 text-text-tertiary data-[state=failed]:text-text-secondary"
    >
      <LogoMark
        className={cn("size-3.5 opacity-80", state === "running" && "motion-safe:animate-pulse")}
      />
      <span className="min-w-0 truncate">{copilotToolActivityLabel(args, state)}</span>
    </p>
  );
}
